import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { SwiperOptions } from 'swiper';
import { CategoryListService } from '../category-deals-list/category-deals-list.service';
import {
    AppliedFilterIds,
    Filter,
    QuickFilter,
    SortLabel,
} from './filter-data.model';
import { ScreenSizeService } from '../services/screen-size.service';
import { FilterDataService } from './filter-data.service';
import { debounce } from 'lodash';
import { ActivatedRoute, NavigationEnd, Router, Scroll } from '@angular/router';
import { FilterApplicationService } from './filter-application.service';
import { ModalOffcanvasComponent } from '@app/controls/modal-offcanvas/modal-offcanvas.component';
import * as Sentry from '@sentry/angular-ivy';
import { distinctUntilChanged, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'md-filter-data',
    templateUrl: './filter-data.component.html',
    styleUrls: ['./filter-data.component.scss'],
})
export class FilterDataComponent implements OnInit, OnDestroy {
    @Input() showClosestDate = false;
    @Input() sortLabel: SortLabel;

    @Output() sort = new EventEmitter<SortLabel>();
    @Output() applyFilters = new EventEmitter<Filter[]>();

    @ViewChild('allFiltersOffcanvas')
    allFiltersOffcanvas: ModalOffcanvasComponent;

    filterOptions: Filter[] = [];
    filterForOpenOverlay: Filter;
    quickFilters: QuickFilter[] = [];
    selectedFilters: Filter[] = [];
    sectionId: number;
    nrOfResults: number;

    categoryListConfig: SwiperOptions = {
        pagination: {
            el: '.swiper-pagination',
            clickable: true,
        },
        navigation: {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
        },
        slidesPerView: 'auto',
        spaceBetween: 8,
        freeMode: true,
    };

    constructor(
        public categoryService: CategoryListService,
        public screenSizeService: ScreenSizeService,
        private filterDataService: FilterDataService,
        private route: ActivatedRoute,
        private router: Router,
        private filterApplicationService: FilterApplicationService,
        private translate: TranslateService
    ) {}
    ngOnDestroy(): void {
        if (this.routerEventsSubscription) {
            this.routerEventsSubscription.unsubscribe();
        }
        if (this.routeParamSubscription) {
            this.routeParamSubscription.unsubscribe();
        }
        if (this.filterResultsSubscription) {
            this.filterResultsSubscription.unsubscribe();
        }
        if (this.langChangedSubscription) {
            this.langChangedSubscription.unsubscribe();
        }
    }

    private routerEventsSubscription: Subscription;
    private routeParamSubscription: Subscription;
    private filterResultsSubscription: Subscription;
    private langChangedSubscription: Subscription;

    get nrOfAppliedFilters(): number {
        return this.selectedFilters.reduce(
            (acc, filter) => acc + filter.filterValues.length,
            0
        );
    }

    ngOnInit(): void {
        this.routerEventsSubscription = this.router.events.subscribe(
            (event) => {
                if (
                    !this.routeParamSubscription &&
                    (event instanceof NavigationEnd || event instanceof Scroll) // This is triggered when the initial page loads
                ) {
                    // Drill into the route tree to find first child with the param
                    let route = this.route;
                    while (route.firstChild) {
                        route = route.firstChild;
                    }

                    this.routeParamSubscription = route.paramMap
                        .pipe(distinctUntilChanged())
                        .subscribe((params) => {
                            const sectionId =
                                Number(params.get('sectionId')) || undefined;

                            this.sectionId = sectionId;
                            this.filterDataService
                                .getFilterData(sectionId)
                                .subscribe((data) => {
                                    this.filterOptions = data.filters;
                                    this.setFiltersBasedOnIds(
                                        this.filterOptions
                                    );
                                });
                            this.filterDataService
                                .getQuickFilters(sectionId)
                                .subscribe((quickFilters) => {
                                    this.quickFilters = quickFilters;
                                    this.setLanguageForQuickFilters();
                                });
                        });

                    this.filterResultsSubscription =
                        this.filterApplicationService.$nrOfResults.subscribe(
                            (nrOfResults) => {
                                this.nrOfResults = nrOfResults;
                            }
                        );
                }
            }
        );
        this.langChangedSubscription = this.translate.onLangChange.subscribe(
            () => {
                this.setLanguageForQuickFilters();
            }
        );
    }

    private setLanguageForQuickFilters() {
        this.quickFilters.forEach((quickFilter) => {
            if (this.translate.currentLang === 'en') {
                quickFilter.filterNameToShow = quickFilter.filterName;
            }
            if (this.translate.currentLang === 'fr') {
                quickFilter.filterNameToShow = quickFilter.filterNameFr;
            }
        });
    }

    quickFilterClicked(quickFilter: QuickFilter) {
        Sentry.captureMessage(
            `Quick filter, section ${this.sectionId}, filterName ${quickFilter.filterName}`,
            {
                level: 'info',
            }
        );
        quickFilter.isSelected = !quickFilter.isSelected;
        if (quickFilter.isSelected) {
            quickFilter.filtersToApply.forEach((filter) => {
                this.selectedFilters.push(filter);
            });
        } else {
            this.selectedFilters = this.selectedFilters.filter((filter) => {
                return !quickFilter.filtersToApply.some(
                    (filterToRemove) =>
                        filterToRemove.filterTypeId === filter.filterTypeId
                );
            });
        }
        this.debounceFilterChangeCall();
    }

    filterOverlayOpen(filter: Filter) {
        this.filterForOpenOverlay = filter;
    }

    openAllFiltersOffcanvas() {
        Sentry.captureMessage(`All filters open, section ${this.sectionId}`, {
            level: 'info',
        });
        this.allFiltersOffcanvas.open();
    }

    filterChanged(filter: Filter) {
        // change filterOptions
        this.filterOptions = this.filterOptions.map((f) => {
            if (f.filterTypeId === filter.filterTypeId) {
                f.filterValues = f.filterValues.map((fv) => {
                    if (fv.filterId === filter.filterValues[0].filterId) {
                        fv.isSelected = filter.filterValues[0].isSelected;
                    }
                    return fv;
                });
                f.isSelected = f.filterValues.some((fv) => fv.isSelected);
            }
            return f;
        });

        const newFilter = {
            ...filter,
            filterValues: filter.filterValues.filter((fv) => fv.isSelected),
        };
        this.selectedFilters = this.selectedFilters.filter(
            (f) => f.filterTypeId !== filter.filterTypeId
        );
        if (filter.isSelected) {
            this.selectedFilters.push(newFilter);
        }
        this.debounceFilterChangeCall();
    }

    applySort(): void {
        this.sort.emit(this.sortLabel);
    }

    setSortLabel(sortLabel: SortLabel) {
        this.sortLabel = sortLabel;
    }

    resetSorting() {
        this.sortLabel = null;
        this.applySort();
    }

    resetFilters() {
        this.selectedFilters = [];
        this.filterOptions.forEach((filter) => {
            filter.filterValues.forEach((filterValue) => {
                filterValue.isSelected = false;
            });
            filter.isSelected = false;
        });
        this.quickFilters.forEach((quickFilter) => {
            quickFilter.isSelected = false;
            quickFilter.filtersToApply.forEach((filter) => {
                filter.filterValues.forEach((filterValue) => {
                    filterValue.isSelected = false;
                });
                filter.isSelected = false;
            });
        });
        this.filterOptions = [...this.filterOptions];
        this.filterApplicationService.changeFilters([]);
    }

    private debounceFilterChangeCall = debounce(() => {
        this.filterApplicationService.changeFilters(this.selectedFilters);
    }, 2000);

    // TODO: this can be optimised to use the filterTypeId too
    private setFiltersBasedOnIds(filterOptions: Filter[]) {
        const appliedFilterIds =
            this.getFilterTypeIdsAndValueIdsFromQueryParams();
        if (appliedFilterIds) {
            filterOptions = filterOptions.map((f) => {
                f.filterValues = f.filterValues.map((fv) => {
                    if (
                        appliedFilterIds
                            .map((idAAF) => idAAF.filterId)
                            .includes(fv.filterId)
                    ) {
                        fv.isSelected = true;
                    }
                    return fv;
                });
                f.isSelected = f.filterValues.some((fv) => fv.isSelected);
                return f;
            });
        }
    }

    private getFilterTypeIdsAndValueIdsFromQueryParams(): AppliedFilterIds[] {
        const queryParams = this.route.snapshot.queryParamMap;
        const filterTypeIdsAndValueIds: AppliedFilterIds[] = [];
        const filtersParam = queryParams.get('filters');
        if (filtersParam) {
            const filterPairs = filtersParam.split(',');
            filterPairs.forEach((pair) => {
                const [filterTypeId, filterValueId] = pair
                    .split('-')
                    .map(Number);
                if (!isNaN(filterTypeId) && !isNaN(filterValueId)) {
                    filterTypeIdsAndValueIds.push({
                        filterTypeId,
                        filterId: filterValueId,
                    });
                }
            });
        }
        return filterTypeIdsAndValueIds;
    }
}
